import {DynamicUi} from 'platform/components';

import {memo, type MouseEvent, type ReactElement, useLayoutEffect, useState} from 'react';
import {createPortal} from 'react-dom';

import type {Nullish} from 'shared/web';

import {useExternalFilterBridge} from '../../hooks/useExternalFilterBridge';
import {useFilterSchema} from '../../hooks/useFilterSchema';
import type {ExternalFilters} from '../../types/ExternalFilters';
import type {FormToDataGridConnectorProps} from '../../types/FormToDataGridConnectorProps';
import {dynamicUiReturnValuesToFormFilterValues} from '../../utils/dynamicUiReturnValuesToFormFilterValues';
import {processFilterValues} from '../../utils/processFilterValues';

export type ExternalFiltersPortalProps = {
  externalFilterId: string;
  /** When omitted, a DynamicUi form is auto-rendered from the BE filter schema. */
  filterComponent?: (props: FormToDataGridConnectorProps) => ReactElement;
  externalFilterResourceId?: string | Nullish;
  externalFilterRecordId?: string | Nullish;
};

const handleContextMenu = (event: MouseEvent) => {
  // Portal events bubble through the React tree to the grid's context-menu suppression handler.
  event.stopPropagation();
};

/**
 * Renders external filter UI into a host DOM element via React portal.
 *
 * Two modes (parity with the old datagrid's CustomFiltersPanelWrapper):
 * - `filterComponent` given → render it (consumer-controlled form).
 * - otherwise → fetch the BE filter schema and render a `DynamicUi` form from it.
 *
 * Store bridging is delegated to `useExternalFilterBridge`.
 */
export const ExternalFiltersPortal = memo((props: ExternalFiltersPortalProps) => {
  const {values, onChange} = useExternalFilterBridge();

  const {schema} = useFilterSchema({
    resourceId: props.externalFilterResourceId,
    recordId: props.externalFilterRecordId,
    enabled: !props.filterComponent,
  });

  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  useLayoutEffect(() => {
    setPortalTarget(document.getElementById(props.externalFilterId));
  }, [props.externalFilterId]);

  if (!portalTarget) {
    return null;
  }

  const FilterComponent = props.filterComponent;

  if (FilterComponent) {
    return createPortal(
      <div className="contents" onContextMenu={handleContextMenu}>
        <FilterComponent values={values} onChange={onChange} />
      </div>,
      portalTarget
    );
  }

  if (schema) {
    const handleDynamicUiChange = (raw: Record<string, unknown>) => {
      const processed = processFilterValues(raw);
      const next = dynamicUiReturnValuesToFormFilterValues(processed) as ExternalFilters;

      // Guard: DynamicUi fires onChange on mount with current values; skip a no-op write.
      if (JSON.stringify(values) === JSON.stringify(next)) {
        return;
      }

      onChange(next);
    };

    return createPortal(
      <div className="contents" onContextMenu={handleContextMenu}>
        <DynamicUi
          key={JSON.stringify(values)}
          schema={schema}
          defaultValues={values}
          onChange={handleDynamicUiChange}
        />
      </div>,
      portalTarget
    );
  }

  return null;
});
